import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faUserTie, 
  faArrowLeft,
  faClock,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../../styles/theme';
import blogService from '../../../services/blogService';

const DetailContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.accent};
    transform: translateX(-4px);
  }
`;

const PostImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    height: 250px;
  }
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.7;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PostTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PostContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.main};
    color: ${props => props.theme.colors.primary};
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  h2 {
    font-size: 1.8rem;
  }
  
  h3 {
    font-size: 1.5rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1rem 0;
  }
  
  blockquote {
    border-left: 4px solid ${props => props.theme.colors.accent};
    padding-left: 1.5rem;
    margin: 2rem 0;
    font-style: italic;
    background-color: rgba(0, 0, 0, 0.02);
    padding: 1.5rem;
    border-radius: 8px;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TagsContainer = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const TagsTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const CTASection = styled.div`
  margin: 3rem 0;
  padding: 3rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.accent} 100%);
  border-radius: 16px;
  text-align: center;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const CTATitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: white;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: white;
  margin-bottom: 2rem;
  opacity: 0.95;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1rem 2.5rem;
  background-color: white;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
    background-color: ${props => props.theme.colors.offWhite};
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }
`;

const ShareableLink = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border-left: 4px solid ${props => props.theme.colors.accent};
`;

const ShareableLinkText = styled.p`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ShareableLinkURL = styled.a`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.accent};
  word-break: break-all;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BlogPostDetail = ({ post, onBack }) => {
  if (!post) {
    return (
      <DetailContainer>
        <ContentContainer>
          <p>Post not found</p>
        </ContentContainer>
      </DetailContainer>
    );
  }

  // Debug logging for CTA


  return (
    <DetailContainer>
      <ContentContainer>
        <BackButton onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Journal
        </BackButton>
        
        {post.featuredImage?.url && (
          <PostImage src={post.featuredImage.url} alt={post.featuredImage.alt} />
        )}
        
        <PostMeta>
          <MetaItem>
            <FontAwesomeIcon icon={faCalendarAlt} />
            {blogService.formatDate(post.date)}
          </MetaItem>
          <MetaItem>
            <FontAwesomeIcon icon={faUserTie} />
            {post.author.name || 'Belvoir Castle'}
          </MetaItem>
          {post.belvoir_meta?.reading_time && (
            <MetaItem>
              <FontAwesomeIcon icon={faClock} />
              {post.belvoir_meta.reading_time}
            </MetaItem>
          )}
        </PostMeta>
        
        <PostTitle>{post.title}</PostTitle>
        
        <PostContent dangerouslySetInnerHTML={{ __html: post.content }} />
        
        {/* CTA Section for Post 319 - Mice Trail */}
        {(post.id === 319 || post.id === '319' || parseInt(post.id) === 319) && (
          <CTASection>
            <CTATitle>🎄 Discover the Magical Mice Trail!</CTATitle>
            <CTADescription>
              Join us this Christmas for an enchanting adventure through Belvoir Castle. 
              Follow the trail and discover the hidden mice throughout our beautiful estate.
            </CTADescription>
            <CTAButton 
              href="https://www.belvoircastle.com/christmas/mice-trail" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Explore the Mice Trail
            </CTAButton>
          </CTASection>
        )}
        
        {/* Shareable Direct Link */}
        <ShareableLink>
          <ShareableLinkText>📎 Direct Link to this Blog Post:</ShareableLinkText>
          <ShareableLinkURL 
            href={`${window.location.origin}/journal/${post.slug || post.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigator.clipboard.writeText(`${window.location.origin}/journal/${post.slug || post.id}`);
              alert('Link copied to clipboard!');
            }}
          >
            {window.location.origin}/journal/{post.slug || post.id}
          </ShareableLinkURL>
        </ShareableLink>
        
        {post.tags && post.tags.length > 0 && (
          <TagsContainer>
            <TagsTitle>
              <FontAwesomeIcon icon={faTag} style={{ marginRight: '0.5rem' }} />
              Tags
            </TagsTitle>
            <TagsList>
              {post.tags.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagsList>
          </TagsContainer>
        )}
      </ContentContainer>
    </DetailContainer>
  );
};

export default BlogPostDetail;